/**
 * Common Zod schemas para SISREP V1.
 * Per data-model V1.1 §1 (Convenciones globales) + ADR-003 (versionado).
 */
import { z } from 'zod';

// ─── IDs (ULID Crockford base32, 26 chars) ─────────────────────────────────

/**
 * ULID validator: 26 chars, Crockford base32 (no I, L, O, U).
 * Per data-model §1.1 + ADR-003.
 */
export const UlidSchema = z
  .string()
  .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'invalid ULID format (26 chars Crockford base32)');

export type Ulid = z.infer<typeof UlidSchema>;

// ─── Versionado append-only fields (mixin) ─────────────────────────────────

/**
 * Campos versionado obligatorios en cada tab versionada.
 * Per data-model §1.2 + ADR-003.
 */
export const VersionedFieldsSchema = z.object({
  entity_id: UlidSchema,
  version_id: UlidSchema,
  is_current: z.boolean(),
  version_of: UlidSchema.nullable(),
  // V1.0.2 F-D1-02: removed .default() — schema NO debe inyectar distrito hardcoded.
  // Multi-distrito V2 require explicit distrito_id en cada write; fallback ocultaría
  // misconfig silenciosa (rows escritas con distrito incorrecto).
  distrito_id: z.string().min(1),
  created_at: z.string().datetime(),
  created_by: z.string().email(),
  created_request_id: UlidSchema,
});

export type VersionedFields = z.infer<typeof VersionedFieldsSchema>;

// ─── Quarter calculation (server-side immutable post-creation) ─────────────

/**
 * Quarter format: YYYY-Qn (n ∈ 1..4).
 * Per data-model §1.5: server-calculated, immutable post-creation.
 */
export const QuarterSchema = z
  .string()
  .regex(/^[0-9]{4}-Q[1-4]$/, 'invalid quarter format (expected YYYY-Qn)');

export type Quarter = z.infer<typeof QuarterSchema>;

/**
 * Computes quarter from date string.
 * Throws if date is invalid.
 */
export function computeQuarter(isoDate: string): Quarter {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`invalid date: ${isoDate}`);
  }
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // 1-12
  const quarter = Math.ceil(month / 3);
  return `${year}-Q${quarter}` as Quarter;
}

// ─── RFC 7807 problem+json error response ──────────────────────────────────

/**
 * Per api-contract.md §3.
 * RFC 7807 + FWA extensions: trace-id, error-code, audit-event-id.
 */
export const ProblemSchema = z.object({
  type: z.string().url().default('about:blank'),
  title: z.string(),
  status: z.number().int().min(100).max(599),
  detail: z.string().optional(),
  instance: z.string().optional(),
  // FWA extensions
  'trace-id': z.string().optional(),
  'error-code': z.string().optional(),
  'audit-event-id': z.string().optional(),
});

export type Problem = z.infer<typeof ProblemSchema>;

// ─── Cursor-based pagination ───────────────────────────────────────────────

/**
 * Cursor-based pagination per api-contract.md §5.
 * Opaque base64-encoded server state.
 */
export const PaginationParamsSchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export type PaginationParams = z.infer<typeof PaginationParamsSchema>;

/**
 * Generic list response shape per api-contract.md §5.
 */
export function listResponseSchema<T extends z.ZodType>(itemSchema: T) {
  return z.object({
    data: z.array(itemSchema),
    next_cursor: z.string().nullable(),
    has_more: z.boolean(),
  });
}

// ─── PII tier framework references (informational) ─────────────────────────

/**
 * PII tier enum per spec §6.1 + pii-config.yaml.
 * Used in runtime tier-aware logic.
 */
export const PiiTierSchema = z.enum(['Crítico', 'Alto', 'Medio', 'Bajo']);
export type PiiTier = z.infer<typeof PiiTierSchema>;

// ─── Roles (per spec §4 + ADR-002) ─────────────────────────────────────────

export const RolSchema = z.enum([
  'admin-distrital',
  'admin-nacional',
  'guiadora-jefa-rama',
  'guiadora-acompañante',
  'comando',
]);
export type Rol = z.infer<typeof RolSchema>;

// ─── access_reason validation (per privacy.md §5) ──────────────────────────

/**
 * Patterns PII a reject en access_reason per privacy.md §5.1.
 */
const PII_REJECT_PATTERNS = [
  // Nombre propio compuesto (2+ palabras capitalizadas con acentos)
  /\b[A-ZÁÉÍÓÚ][a-záéíóú]{2,}\s+[A-ZÁÉÍÓÚ][a-záéíóú]{2,}\b/,
  // Fecha DD/MM/AAAA
  /\b\d{1,2}\/\d{1,2}\/\d{4}\b/,
  // Fecha ISO
  /\b\d{4}-\d{2}-\d{2}\b/,
  // Teléfono MX
  /\b(\+52\s?)?\d{2,3}[-\s]?\d{3,4}[-\s]?\d{4}\b/,
  // Email
  /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/,
  // CURP
  /\b[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d\b/,
];

export const AccessReasonSchema = z
  .string()
  .min(20, 'access_reason must be ≥20 chars')
  .max(500, 'access_reason must be ≤500 chars')
  .refine(
    (value) => !PII_REJECT_PATTERNS.some((pattern) => pattern.test(value)),
    'access_reason cannot contain PII patterns (nombres propios, fechas, teléfonos, emails, CURPs)',
  );

export type AccessReason = z.infer<typeof AccessReasonSchema>;
